import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Statistic, Row, Col, message } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

const Conversions = () => {
  const [loading, setLoading] = useState(false);
  // Performance
  const [perfRows, setPerfRows] = useState([]);
  // Conversions
  const [convRows, setConvRows] = useState([]);
  // ROI
  const [roiRows, setRoiRows] = useState([]);
  const [roiSummary, setRoiSummary] = useState({ mrr: 0, arr: 0, arpu: 0 });
  const { token } = useAuth();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [perfRes, roiRes] = await Promise.all([
          fetch('/api/analytics/campaign-performance', { headers }),
          fetch('/api/analytics/revenue-reports?months=6', { headers })
        ]);
        if (!perfRes.ok) throw new Error('Failed to load campaign performance');
        if (!roiRes.ok) throw new Error('Failed to load revenue reports');
        const perfJson = await perfRes.json();
        const roiJson = await roiRes.json();

        const items = Array.isArray(perfJson?.items) ? perfJson.items : [];
        // Normalize performance rows
        const perf = items.map(it => ({
          name: it.name,
          impressions: it.impressions ?? 0,
          clicks: it.clicks ?? 0,
          ctr: typeof it.ctr === 'number' ? `${it.ctr}%` : (it.ctr ?? '0%'),
          cpa: typeof it.cpa === 'number' ? `$${it.cpa}` : (it.cpa ?? '—'),
          conversions: it.conversions ?? 0,
          conversion_value: it.conversion_value ?? 0,
        }));
        setPerfRows(perf);

        // Derive conversions rows: compute CVR from clicks and conversions
        const conv = items.map(it => {
          const clicks = Number(it.clicks || 0);
          const conversions = Number(it.conversions || 0);
          const cvr = clicks > 0 ? ((conversions / clicks) * 100) : 0;
          const cpa = typeof it.cpa === 'number' ? it.cpa : Number(it.cpa || 0);
          return {
            name: it.name,
            conversions,
            cvr: `${cvr.toFixed(2)}%`,
            cpa: isNaN(cpa) ? '—' : `$${cpa}`,
          };
        });
        setConvRows(conv);

        // ROI rows: use revenue reports monthly deltas
        const rItems = Array.isArray(roiJson?.items) ? roiJson.items : [];
        setRoiRows(rItems);
        setRoiSummary(roiJson?.summary || { mrr: 0, arr: 0, arpu: 0 });
      } catch (e) {
        message.error(e?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const perfTotals = useMemo(() => {
    const impressions = perfRows.reduce((s, r) => s + Number(r.impressions || 0), 0);
    const clicks = perfRows.reduce((s, r) => s + Number(r.clicks || 0), 0);
    const ctr = impressions > 0 ? ((clicks / impressions) * 100) : 0;
    return { impressions, clicks, ctr: `${ctr.toFixed(2)}%` };
  }, [perfRows]);

  const convTotals = useMemo(() => {
    const conversions = convRows.reduce((s, r) => s + Number(r.conversions || 0), 0);
    // average CVR and CPA across campaigns
    const avgCvr = convRows.length ? (convRows.reduce((s, r) => s + Number((r.cvr || '0').toString().replace('%','')), 0) / convRows.length) : 0;
    const avgCpa = convRows.length ? (convRows.reduce((s, r) => s + Number((r.cpa || '$0').toString().replace('$','')), 0) / convRows.length) : 0;
    return { conversions, avgCvr: `${avgCvr.toFixed(2)}%`, avgCpa: `$${avgCpa.toFixed(2)}` };
  }, [convRows]);

  const performanceColumns = [
    { title: 'Campaign', dataIndex: 'name', key: 'name' },
    { title: 'Impressions', dataIndex: 'impressions', key: 'impressions' },
    { title: 'Clicks', dataIndex: 'clicks', key: 'clicks' },
    { title: 'CTR', dataIndex: 'ctr', key: 'ctr' },
    { title: 'CPA', dataIndex: 'cpa', key: 'cpa' },
  ];

  const conversionsColumns = [
    { title: 'Campaign', dataIndex: 'name', key: 'name' },
    { title: 'Conversions', dataIndex: 'conversions', key: 'conversions' },
    { title: 'CVR', dataIndex: 'cvr', key: 'cvr' },
    { title: 'CPA', dataIndex: 'cpa', key: 'cpa' },
  ];

  const roiColumns = [
    { title: 'Month', dataIndex: 'month', key: 'month' },
    { title: 'New MRR', dataIndex: 'new_mrr', key: 'new_mrr', render: v => `$${Number(v || 0).toLocaleString()}` },
    { title: 'Churn', dataIndex: 'churn', key: 'churn', render: v => `$${Number(v || 0).toLocaleString()}` },
    { title: 'Net MRR', dataIndex: 'net_mrr', key: 'net_mrr', render: v => `$${Number(v || 0).toLocaleString()}` },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Analytics' }, { title: 'Conversions' }]} />

      {/* Performance Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Campaign Performance</Title>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}><Card><Statistic title="Total Impressions" value={perfTotals.impressions} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="Total Clicks" value={perfTotals.clicks} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="Avg. CTR" value={perfTotals.ctr} /></Card></Col>
      </Row>

      <Card style={{ marginBottom: 24 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Campaign KPIs overview.</Paragraph>
        <Table rowKey="name" columns={performanceColumns} dataSource={perfRows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      {/* Conversions Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Conversion Tracking</Title>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}><Card><Statistic title="Total Conversions" value={convTotals.conversions} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="Avg. CVR" value={convTotals.avgCvr} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="Avg. CPA" value={convTotals.avgCpa} /></Card></Col>
      </Row>

      <Card style={{ marginBottom: 24 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Conversions by campaign.</Paragraph>
        <Table rowKey="name" columns={conversionsColumns} dataSource={convRows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      {/* ROI Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>ROI Reports</Title>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}><Card><Statistic title="MRR" value={roiSummary.mrr} prefix="$" /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="ARR" value={roiSummary.arr} prefix="$" /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title="ARPU" value={roiSummary.arpu} prefix="$" /></Card></Col>
      </Row>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Monthly revenue deltas.</Paragraph>
        <Table rowKey="month" columns={roiColumns} dataSource={roiRows} loading={loading} pagination={{ pageSize: 12 }} />
      </Card>
    </div>
  );
};

export default Conversions;
